import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, LayoutDashboard, FileText, Users, Settings } from "lucide-react";
import { db, auth } from "@/firebase/config";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { useToast } from "@/components/ui/use-toast";
import { DisciplineDashboard } from "./discipline/DisciplineDashboard";
import { IncidentReporter } from "./discipline/IncidentReporter";
import { CaseManager } from "./discipline/CaseManager";
import { StudentDirectory } from "./discipline/StudentDirectory";

interface DisciplineManagementProps {
  onBack: () => void;
}

const DisciplineManagement = ({ onBack }: DisciplineManagementProps) => {
  const [view, setView] = useState<'dashboard' | 'report' | 'cases' | 'students'>('dashboard');
  const [schoolId, setSchoolId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Auth & Claims Loaded
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
            const token = await getIdTokenResult(user);
            const claims = token.claims;
            // Simple logic for primary school ID
            const sid = (claims.schoolID as string) || 
                        (claims.defaultAffiliation as any)?.schoolID ||
                        (claims.affiliations as any[])?.[0]?.schoolID;
            
            setSchoolId(sid);
        } catch (e) {
            console.error("Auth error", e);
        }
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
      return <div className="flex h-screen items-center justify-center">Loading...</div>;
  }

  if (!schoolId) {
      return <div className="p-8 text-center">School ID not found. Please log in again.</div>;
  }

  return (
    <div className="flex-1 p-4 sm:p-6 overflow-auto bg-gray-50 dark:bg-gray-950 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
        <div>
            <Button variant="ghost" onClick={onBack} className="mb-2 pl-0 hover:bg-transparent hover:text-blue-600">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
            </Button>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Discipline & Behavior</h1>
            <p className="text-muted-foreground mt-1">Manage incidents, cases, and restorative interventions.</p>
        </div>
        <div className="flex gap-2">
            <Button onClick={() => setView('report')} className="bg-blue-600 hover:bg-blue-700">
                <FileText className="h-4 w-4 mr-2" />
                Report Incident
            </Button>
        </div>
      </div>

      {/* Main Content Area */}
      {view === 'report' ? (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
             <Button variant="ghost" onClick={() => setView('dashboard')} className="mb-4">
                 <ArrowLeft className="h-4 w-4 mr-2" /> Cancel Report
             </Button>
             <IncidentReporter 
                schoolId={schoolId} 
                onSuccess={() => setView('dashboard')} 
                onCancel={() => setView('dashboard')} 
             />
          </div>
      ) : (
          <Tabs defaultValue="dashboard" className="space-y-6" value={view} onValueChange={(v: any) => setView(v)}>
            <TabsList className="bg-white dark:bg-slate-900 p-1 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm">
                <TabsTrigger value="dashboard" className="px-4 py-2 text-sm">
                    <LayoutDashboard className="h-4 w-4 mr-2" /> Overview
                </TabsTrigger>
                <TabsTrigger value="cases" className="px-4 py-2 text-sm">
                    <FileText className="h-4 w-4 mr-2" /> Case Management
                </TabsTrigger>
                <TabsTrigger value="students" className="px-4 py-2 text-sm">
                    <Users className="h-4 w-4 mr-2" /> Students
                </TabsTrigger>
                <TabsTrigger value="settings" className="px-4 py-2 text-sm">
                    <Settings className="h-4 w-4 mr-2" /> Settings
                </TabsTrigger>
            </TabsList>

            <TabsContent value="dashboard" className="space-y-4 outline-none">
                <DisciplineDashboard 
                    schoolId={schoolId} 
                    onViewAll={() => setView('cases')}
                    onReportIncident={() => setView('report')}
                />
            </TabsContent>

            <TabsContent value="cases" className="space-y-4 outline-none">
                <CaseManager schoolId={schoolId} />
            </TabsContent>

            <TabsContent value="students" className="outline-none">
                <StudentDirectory schoolId={schoolId} />
            </TabsContent>
          </Tabs>
      )}
    </div>
  );
};

export default DisciplineManagement;
